import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { Button, notify, TextField } from '@speeki/global-ui-components';
import { StorageManager } from '@speeki/storage-manager';
import { ALL_DIGIST_REGEX } from '@speeki/utils';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import {
    CheckIdNumberAndPinDocument,
    CheckIdNumberAndPinUnloggedDocument,
} from '@graphql/generated/graphql';

import { getPath } from '@services/RouteService/paths';

import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { authVar, guestPinVar } from '@boot/reactiveVariables';
import { yupResolver } from '@hookform/resolvers/yup';

interface CheckStatusFormProps {
    onSuccess?: () => void;
}

export const CheckStatusForm = ({ onSuccess }: CheckStatusFormProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        formState: { errors, isSubmitted, isSubmitting, isValid },
        handleSubmit,
        register,
    } = useForm<CheckStatusFormValues>({
        mode: 'onChange',
        resolver: yupResolver(CheckStatusFormSchema),
    });

    const isGuest = useReactiveVar(authVar).isGuest;
    const isError = !!Object.keys(errors).length;

    const tenantUuid = StorageManager.getValue('tenantUuid') || '';

    const [checkIdNumberAndPin, { loading: guestLoading }] = useLazyQuery(
        CheckIdNumberAndPinDocument,
        {
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true,
            //TODO: investigate error issue after second call SPD-2998
            onCompleted: (data) => {
                if (!data) return;

                navigate(
                    getPath({
                        params: {
                            caseId: data?.case?.caseId,
                        },
                        path: '/incidents/reports/:caseId',
                    }),
                );
            },
            onError: (error) => {
                notify(error.message).ERROR();
                console.error('useCheckIdNumberAndPin :: ', { error });
            },
        },
    );

    const [checkIdNumberAndPinUnlogged, { loading: unloggedLoading }] =
        useLazyQuery(CheckIdNumberAndPinUnloggedDocument, {
            context: {
                notifyOnError: true,
            },
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true,
            onCompleted: (data) => {
                if (!data) return;

                onSuccess?.();
                navigate(
                    getPath({
                        params: {
                            caseId: data?.case?.caseId,
                        },
                        path: '/incidents/reports/:caseId',
                    }),
                );
            },
        });

    const onSubmit = (data: CheckStatusFormValues) => {
        const { caseId, pin } = data;
        if (!pin) return;

        guestPinVar(pin);

        if (isGuest) {
            void checkIdNumberAndPin({
                variables: {
                    input: {
                        caseId,
                        pinCode: pin,
                    },
                },
            });
        } else {
            void checkIdNumberAndPinUnlogged({
                variables: {
                    input: {
                        caseId,
                        pinCode: pin,
                        tenantUuid,
                    },
                },
            });
        }
    };

    return (
        <StandardForm>
            <TextField
                {...register('caseId')}
                className="htmlToImageIgnore"
                error={!!errors.caseId}
                label={t('REPORT_ID')}
                message={
                    errors.caseId?.message
                        ? t(errors.caseId?.message)
                        : undefined
                }
                name={'caseId'}
                placeholder={t('REPORT_ID_PLACEHOLDER')}
                testId="Xh_IhQW"
            />
            <TextField
                {...register('pin')}
                className="htmlToImageIgnore"
                error={!!errors.pin}
                label={t('REPORT_PIN')}
                message={
                    errors.pin?.message ? t(errors.pin?.message) : undefined
                }
                name={'pin'}
                placeholder={t('REPORT_PIN_PLACEHOLDER')}
                testId="sRxMnft"
                type={'password'}
            />
            <StandardForm.Actions>
                <Button
                    disabled={
                        guestLoading ||
                        unloggedLoading ||
                        isSubmitting ||
                        (!isValid && isSubmitted) ||
                        isError
                    }
                    fullWidth
                    isLoading={guestLoading || unloggedLoading || isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    testId="button_check-case-status"
                    variant="primary"
                >
                    {t('CHECK_CASE_STATUS')}
                </Button>
            </StandardForm.Actions>
        </StandardForm>
    );
};

const CheckStatusFormSchema = yup.object({
    caseId: yup.string().required('FIELD_CANNOT_BE_EMPTY'),
    pin: yup
        .string()
        .required('FIELD_CANNOT_BE_EMPTY')
        .matches(ALL_DIGIST_REGEX, {
            message: 'PIN_NUMBERS_ONLY',
        })
        .length(6, 'PIN_EQUAL_TO_6'),
});

type CheckStatusFormValues = yup.InferType<typeof CheckStatusFormSchema>;
